import { MainLayout } from '../../Components/Layouts/MainLayout/MainLayout';
import { useTranslation } from 'react-i18next';
import SearchBar from '../../Components/SearchBar/SearchBar';
import { useDebounce } from '../../Hooks/useDebounce';
import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Paper } from '@mui/material';
import { getClients } from '../../Services/Client';
import { ClientTable } from './ClientTable';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { COUNT_ROWS_PER_PAGE } from '../../Types/constants';
import { Loader } from '../../Components/Loader/Loader';

export const Clients = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [currentPage, setCurrentPage] = useState(0);
  const [clientList, setClientList] = useState<Client[]>([]);

  const title = t('client.title');

  const fetchClients = useCallback(
    async ({ pageParam = 1 }) => {
      return await getClients({
        page: pageParam,
        count: COUNT_ROWS_PER_PAGE,
        name: debouncedSearchTerm,
      });
    },
    [debouncedSearchTerm],
  );

  const {
    data: tableData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['getClients', debouncedSearchTerm],
    queryFn: fetchClients,
    getNextPageParam: (lastPage) => {
      const nextPage = (lastPage.page || 0) + 1;
      return nextPage <= (lastPage.lastPage || 0) ? nextPage : undefined;
    },
    initialPageParam: 1,
  });

  useEffect(() => {
    setCurrentPage(0);
    refetch();
  }, [debouncedSearchTerm, refetch]);

  useEffect(() => {
    const allClients = tableData?.pages.flatMap((page) => page.items) || [];
    const existingIds = new Set(clientList.map((client) => client.id));
    const newItems = allClients.filter((client) => !existingIds.has(client.id));

    if (clientList.length > 0 && newItems.length > 0) {
      setClientList((prev) => [...prev, ...newItems]);
    } else if (newItems.length > 0) {
      setClientList(newItems);
    } else if (allClients.length === 0) {
      setClientList([]);
    }
  }, [tableData]);

  const totalItems = tableData?.pages[tableData.pages.length - 1]?.totalItems || 0;

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  return (
    <div>
      <MainLayout
        onClientsInvalidateQuery={() =>
          queryClient.invalidateQueries({
            queryKey: ['getClients', debouncedSearchTerm],
          })
        }
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <h1>{title}</h1>
        </Box>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <SearchBar margin="0 0 15px 0" onSearch={handleSearch} />
        </div>
        {tableData && (
          <Paper
            sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            <ClientTable
              rows={clientList}
              loadMore={fetchNextPage}
              loading={isLoading}
              hasMore={hasNextPage}
              totalItems={totalItems}
            />
          </Paper>
        )}
        {isFetchingNextPage && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        <Loader isLoading={isLoading} />
      </MainLayout>
    </div>
  );
};
